import React, { useState, useEffect } from "react";
import { Link as LinkIcon, Send } from "lucide-react";

/**
 * Props:
 *  - onSubmit(formData)  → formData.leaveType is LeaveType._id
 *  - loading
 *  - leaveTypes        → array from GET /api/leave-types-portal/active
 *  - leaveTypesLoading
 */
export default function ApplyLeaveForm({
  onSubmit,
  loading,
  leaveTypes = [],
  leaveTypesLoading = false,
}) {
  const [formData, setFormData] = useState({
    leaveType: "", // ObjectId string
    startDate: "",
    endDate: "",
    reason: "",
    attachmentUrl: "",
  });

  // When types load, default to first option if still empty
  useEffect(() => {
    if (!formData.leaveType && leaveTypes.length > 0) {
      setFormData((prev) => ({ ...prev, leaveType: leaveTypes[0]._id }));
    }
  }, [leaveTypes, formData.leaveType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const leaveTypeId = String(formData.leaveType || "").trim();
    if (!leaveTypeId || leaveTypeId.length !== 24) {
      return; // parent will also validate
    }
    onSubmit({
      leaveType: leaveTypeId, // backend expects ObjectId
      startDate: formData.startDate,
      endDate: formData.endDate,
      reason: formData.reason.trim(),
      attachmentUrl: formData.attachmentUrl || "",
    });
    setFormData({
      leaveType: leaveTypes[0]?._id || "",
      startDate: "",
      endDate: "",
      reason: "",
      attachmentUrl: "",
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-gray-100 dark:border-neutral-800 shadow-sm space-y-4"
    >
      <h3 className="text-lg font-bold text-gray-800 dark:text-neutral-100 mb-2">Apply for Leave</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase mb-1">
            Leave Type
          </label>
          <select
            name="leaveType"
            value={formData.leaveType}
            onChange={handleChange}
            className="w-full border p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 focus:bg-white transition"
            required
            disabled={leaveTypesLoading || leaveTypes.length === 0}
          >
            <option value="">
              {leaveTypesLoading ? "Loading…" : "Select leave type"}
            </option>
            {leaveTypes.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name} ({t.code})
                {t.isPaid === false ? " — Unpaid" : ""}
              </option>
            ))}
          </select>
          {!leaveTypesLoading && leaveTypes.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              No active leave types. Ask admin to create them first.
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase mb-1">
            Start Date
          </label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            className="w-full border p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 focus:bg-white transition"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase mb-1">
            End Date
          </label>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            className="w-full border p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 focus:bg-white transition"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase mb-1">
          Reason for Leave
        </label>
        <textarea
          name="reason"
          rows={3}
          value={formData.reason}
          onChange={handleChange}
          placeholder="State reason clearly..."
          className="w-full border p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 focus:bg-white transition"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase mb-1">
          Attachment URL (Optional)
        </label>
        <div className="relative">
          <LinkIcon className="w-4 h-4 text-gray-400 dark:text-neutral-500 absolute left-3 top-3.5" />
          <input
            type="url"
            name="attachmentUrl"
            value={formData.attachmentUrl}
            onChange={handleChange}
            placeholder="https://example.com/document.pdf"
            className="w-full border p-3 pl-10 rounded-xl bg-gray-50 dark:bg-neutral-950 focus:bg-white transition"
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-2">
        <p className="text-xs text-gray-500 dark:text-neutral-400 italic">
          * Working days are calculated automatically (excludes Sundays & holidays)
        </p>
        <button
          type="submit"
          disabled={loading || leaveTypes.length === 0}
          className="bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 font-medium flex items-center gap-2 w-full md:w-auto justify-center transition disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
          {loading ? "Submitting..." : "Apply for Leave"}
        </button>
      </div>
    </form>
  );
}